"use client";

import { useEffect, useState } from "react";
import { HexColorPicker } from "react-colorful";

import { Button } from "@/components/ui/button";
import AlertMsg from "@/components/dialogs/common/AlertMsg";
import { useUpdateTenantBrandingMutation } from "@/store/slices/apiSlice";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDispatch } from "react-redux";
import { setPrimaryColor } from "@/store/slices";

const initAlertMsg = {
  open: false,
  label: "",
  msg: "",
  isError: false,
  isLoading: false,
  btnText: "",
  canClose: false,
};

const brandColors = ["#FFFFFF", "#000000", "#9CA3AF", "#8B5CF6", "#00D3F2"];

const isValidHex = (value) => /^#([0-9A-Fa-f]{6})$/.test(value);

const ChangeBrandColorsDialog = ({
  open,
  onClose,
  tenantId,
  primaryColor = "",
  secondaryColor = "",
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedColorType, setSelectedColorType] = useState("primary");
  const [showPicker, setShowPicker] = useState(false);

  const [formColors, setFormColors] = useState({
    primaryColor,
    secondaryColor,
  });
  const isAddMode = !primaryColor && !secondaryColor;

  const [alertMsg, setAlertMsg] = useState(initAlertMsg);
  const [updateTenantBranding] = useUpdateTenantBrandingMutation();
  useEffect(() => {
    if (open) {
      setFormColors({
        primaryColor,
        secondaryColor,
      });

      setSelectedColorType("primary");
      setShowPicker(false);
    }
  }, [open, primaryColor, secondaryColor]);

  const activeColorKey =
    selectedColorType === "primary" ? "primaryColor" : "secondaryColor";

  const activeColor = formColors[activeColorKey];

  const updateActiveColor = (value) => {
    setFormColors((prev) => ({
      ...prev,
      [activeColorKey]: value,
    }));
  };

  const handleHexInputChange = (value) => {
    const cleanedValue = value.trim();

    if (!cleanedValue) {
      updateActiveColor("#");
      return;
    }

    const normalizedValue = cleanedValue.startsWith("#")
      ? cleanedValue
      : `#${cleanedValue}`;

    updateActiveColor(normalizedValue.slice(0, 7));
  };

  const handlePresetColor = (color) => {
    updateActiveColor(color);
  };

  const handleClose = () => {
    if (isLoading) return;

    setShowPicker(false);
    onClose?.();
  };

  const handleAlertClose = () => {
    const wasError = alertMsg.isError;

    setAlertMsg(initAlertMsg);

    if (!wasError) {
      setShowPicker(false);
      onClose?.();
    }
  };

  const handleSubmit = async () => {
    if (!isValidHex(formColors.primaryColor)) {
      setAlertMsg({
        open: true,
        label: "Invalid Primary Color",
        msg: "Please enter a valid primary color in hexadecimal format, for example #00D3F2.",
        isError: true,
        btnText: "Close",
        canClose: false,
      });
      return;
    }

    if (!isValidHex(formColors.secondaryColor)) {
      setAlertMsg({
        open: true,
        label: "Invalid Secondary Color",
        msg: "Please enter a valid secondary color in hexadecimal format, for example #6366F1.",
        isError: true,
        btnText: "Close",
        canClose: false,
      });
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        primaryColor: formColors.primaryColor,
        secondaryColor: formColors.secondaryColor,
      };

      /*
        Replace this fake response with your real API mutation later:

        const res = await updateBrandColors(payload).unwrap();
      */

      const res = await updateTenantBranding({
        tenantId,
        payload,
      }).unwrap();
      if (res?.response?.statusEnum === 1) {
        const latestPrimaryColor =
          res?.response?.data?.branding?.primaryColor ||
          res?.response?.data?.primaryColor ||
          payload.primaryColor;

        // dispatch(setPrimaryColor(latestPrimaryColor));
        dispatch(
          setPrimaryColor({
            tenantId,
            primaryColor: latestPrimaryColor,
          }),
        );
        localStorage.setItem(`tenant-primary-colors-${tenantId}`, latestPrimaryColor);
        // onSuccess?.(payload);

        onSuccess?.({
          ...payload,
          primaryColor: latestPrimaryColor,
        });

        setAlertMsg({
          open: true,
          label: "Brand Colors Updated Successfully",
          msg:
            res?.response?.message ||
            "Brand colors have been updated successfully.",
          isError: false,
          btnText: "Close",
          canClose: false,
        });
      } else {
        setAlertMsg({
          open: true,
          label: "Update Failed",
          msg:
            res?.response?.message ||
            "Unable to update brand colors. Please try again.",
          isError: true,
          btnText: "Close",
          canClose: false,
        });
      }
    } catch (err) {
      setAlertMsg({
        open: true,
        label: "Update Failed",
        msg:
          err?.data?.data?.response?.message ||
          err?.data?.response?.message ||
          "Something went wrong. Please try again.",
        isError: true,
        btnText: "Close",
        canClose: false,
      });

      console.error("Update brand colors error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          className="w-[calc(100vw-32px)] max-w-[820px] max-h-[92vh] overflow-y-auto rounded-lg
            border bg-brand-19 border-brand-11 px-5 py-8 text-neutral-100 shadow-2xl sm:px-10 lg:px-14"
        >
          <DialogHeader className="mb-7 text-left">
            <DialogTitle className="text-left text-2xl font-bold text-[#67D8FF]">
              {/* Edit Brand Colors */}
              {isAddMode ? "Add Brand Colors" : "Change Brand Colors"}
            </DialogTitle>

            <DialogDescription className="sr-only">
              Edit primary and secondary brand colors
            </DialogDescription>
          </DialogHeader>

          <div
            className={`grid grid-cols-1 gap-8 ${
              showPicker ? "lg:grid-cols-[1fr_310px]" : "lg:grid-cols-1"
            }`}
          >
            <div className="space-y-6">
              <BrandColorInput
                label="Add Primary Color"
                value={formColors.primaryColor}
                active={selectedColorType === "primary"}
                onChangeClick={() => {
                  setSelectedColorType("primary");
                  setShowPicker(true);
                }}
              />

              {/* <BrandColorInput
                label="Add Secondary / Accent Color (Optional)"
                value={formColors.secondaryColor}
                active={selectedColorType === "secondary"}
                onChangeClick={() => {
                  setSelectedColorType("secondary");
                  setShowPicker(true);
                }}
              /> */}
            </div>

            {showPicker && (
              <div className="rounded-none border border-[#006D80] bg-[#1A2236] p-5">
                <HexColorPicker
                  color={isValidHex(activeColor) ? activeColor : "#00D3F2"}
                  onChange={updateActiveColor}
                  className="!w-full"
                />

                <div className="mt-4 flex items-center gap-3">
                  <span
                    className="h-9 w-9 shrink-0 rounded-md border border-white/40"
                    style={{
                      backgroundColor: isValidHex(activeColor)
                        ? activeColor
                        : "transparent",
                    }}
                  />

                  <input
                    value={activeColor}
                    onChange={(e) => handleHexInputChange(e.target.value)}
                    placeholder="#00D3F2"
                    maxLength={7}
                    className="
                      w-full
                      h-10
                      flex-1
                      rounded-md
                      border
                      border-[#14708A]
                      bg-[#151515]
                      px-3
                      text-sm
                      font-bold
                      text-neutral-100
                      outline-none
                      placeholder:text-neutral-100/40
                      focus:border-[#00D7F2]
                    "
                  />
                </div>

                {!isValidHex(activeColor) && (
                  <p className="mt-2 text-xs text-red-400">
                    Enter a valid hex color, e.g. #00D3F2
                  </p>
                )}

                <p className="mt-4 text-center text-sm text-neutral-100/70">
                  Brand colors:
                </p>

                <div className="mt-4 flex items-center justify-center gap-4">
                  {brandColors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => handlePresetColor(color)}
                      className={`h-7 w-7 rounded-full border-2 ${
                        activeColor?.toLowerCase() === color.toLowerCase()
                          ? "border-white"
                          : "border-transparent"
                      }`}
                      style={{ backgroundColor: color }}
                      aria-label={`Choose ${color}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="
                h-11
                w-full
                border-[#607A9B]
                bg-[#223957]
                px-8
                font-bold
                text-neutral-100
                hover:bg-[#2B466B]
                hover:text-neutral-100
                sm:w-auto
              "
            >
              Go back
            </Button>

            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="
                h-11
                w-full
                bg-brand-21
                px-8
                font-bold
                text-neutral-100
                hover:bg-brand-22
                sm:w-auto
              "
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertMsg
        label={alertMsg.label}
        msg={alertMsg.msg}
        open={alertMsg.open}
        onClose={handleAlertClose}
        confirmAction={handleAlertClose}
        isLoading={false}
        isError={alertMsg.isError}
        canClose={alertMsg.canClose}
        confirmationBtnText={alertMsg.btnText}
      />
    </>
  );
};

const BrandColorInput = ({ label, value, active, onChangeClick }) => {
  return (
    <div>
      <p className="mb-3 text-sm font-bold text-neutral-100">{label}</p>

      <div className="flex h-16 overflow-hidden rounded-md border border-[#14708A] bg-[#151515]">
        <div
          className={`flex flex-1 items-center gap-4 px-4 text-left ${
            active ? "ring-1 ring-[#00D3F2]" : ""
          }`}
        >
          <span
            className="h-9 w-9 shrink-0 rounded-full border-2 border-white"
            style={{
              backgroundColor: isValidHex(value) ? value : "transparent",
            }}
          />

          <span className="text-base font-bold text-neutral-100">
            {value || "No color selected"}
          </span>
        </div>

        <button
          type="button"
          onClick={onChangeClick}
          className="w-[120px] border-l border-[#14708A] text-sm font-bold text-[#00D3F2] hover:bg-white/5"
        >
          Change
        </button>
      </div>
    </div>
  );
};

export default ChangeBrandColorsDialog;
